import * as _ from "lodash";

import { ExtensionBase } from "../extensionBase";
import { ModelApi } from "../../api/modelApi";
import { viewerOptions } from "../../viewer";
import * as SmartDrawings from "../../smartDrawings";
import { GfxUtils } from "../../common/gfxUtils";
import { UiUtils } from "../../common/uiUtils";

const lineMaterial = GfxUtils.createLineMaterial("FF0000");
const dashMaterial = GfxUtils.createLineMaterial("00FF00");

const LINE_WIDTH = 3;
const DASH_SIZE = 50;
const GAP_SIZE = 100;
const MEASURE_LINE_OFFSET = 500;

export class MeasurementExtension extends ExtensionBase {

    static extensionName = "MeasurementExtension";
    private overlayName = "measurements";

    private subToolbar: Autodesk.Viewing.UI.ControlGroup;
    private measureButton: Autodesk.Viewing.UI.Button;

    private gfx: GfxUtils;

    private endpoints = [];
    private labels = [];

    private displaying = false;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);
        this.onModelRootLoaded = this.onModelRootLoaded.bind(this);
        this.onCameraChange = this.onCameraChange.bind(this);

        viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.onCameraChange);

        this.gfx = new GfxUtils(viewer, this.overlayName);
        (viewer.impl as any).createOverlayScene(
            this.overlayName);
    }

    static registerExtension() {
        super.registerExtension(MeasurementExtension.extensionName, MeasurementExtension);
    }

    onToolbarCreated(): void {
        
        this.measureButton = UiUtils.createButton({
            id: "SmartDrawings.MeasurementButton",
            tooltip: "View measurements",
            iconClass: "ion-md-calculator",
            onClick: () => {
                this.toggleMeasurements();
            }
        });
       
        this.subToolbar = this.getControlGroup("SmartDrawings.Extensions");
        this.subToolbar.addControl(this.measureButton);
        
    }

    onModelRootLoaded(): void {
    }

    private async toggleMeasurements() {
        if (this.displaying) {
            this.removeMeasurements();
            this.measureButton.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
            this.displaying = false;
        } else {
            await this.addMeasurements();
            this.measureButton.setState(Autodesk.Viewing.UI.Button.State.ACTIVE);
            this.displaying = true;
        }
    }

    private async addMeasurements() {
        const { data } = await ModelApi.getMeasurements(viewerOptions.urn);
        for (let m of data) {
            this.addMeasurement(m);
        }
        //this.drawAxis();
        this.onCameraChange();
    }

    private removeMeasurements() {
        this.gfx.clear();
        (this.viewer.impl as any).clearOverlay(this.overlayName);
        this.removeLabels(this.endpoints);
        this.removeLabels(this.labels);
        this.endpoints = [];
        this.labels = [];
    }

    private removeLabels(labels) {
        for (let label of labels) {
            this.viewer.container.removeChild(label.label);
        }
    }

    private minAbsPair(obj) {
        return Object.keys(obj).map(k => {
            return [k, obj[k]];
        }).reduce((a, b) => {
            return Math.abs(b[1]) < Math.abs(a[1]) ? b : a;
        });
    }

    private createPerpendicularOffsetPoints(p1: THREE.Vector3, p2: THREE.Vector3): THREE.Vector3[] {
        const center = (this.viewer.model as any).getVisibleBounds(true).center();

        //const s = p2.clone().sub(p1);
        const axis = this.minAbsPair(_.pick(this.midPoint(p1, p2), ["x", "y", "z"]))[0];

        const p1normal = new THREE.Vector3();
        const p2normal = new THREE.Vector3();

        const p1plane = p1.clone();
        p1plane[axis] += 1;

        const p2plane = p2.clone();
        p2plane[axis] += 1;

        const triangle1 = new THREE.Triangle(p1, p2, p1plane);
        (triangle1 as any).normal(p1normal);

        const triangle2 = new THREE.Triangle(p1, p2, p2plane);
        (triangle2 as any).normal(p2normal);

        const p1a = p1normal.clone().multiplyScalar(MEASURE_LINE_OFFSET).add(p1);
        const p2a = p2normal.clone().multiplyScalar(MEASURE_LINE_OFFSET).add(p2);

        const p1b = p1normal.clone().multiplyScalar(-MEASURE_LINE_OFFSET).add(p1);
        const p2b = p2normal.clone().multiplyScalar(-MEASURE_LINE_OFFSET).add(p2);

        if (this.midPoint(p1a, p2a).distanceTo(center) > this.midPoint(p1b, p2b).distanceTo(center)) {
            return [p1a, p2a];
        } else {
            return [p1b, p2b];
        }
    }

    private addMeasurement({ point1, point2, linePoint1, linePoint2, value }: SmartDrawings.Measurement) {
        const p1 = new THREE.Vector3(point1.x, point1.y, point1.z);
        const p2 = new THREE.Vector3(point2.x, point2.y, point2.z);

        let lineP1: THREE.Vector3, lineP2: THREE.Vector3;
        if (linePoint1 && linePoint2) {
            lineP1 = new THREE.Vector3(linePoint1.x, linePoint1.y, linePoint1.z);
            lineP2 = new THREE.Vector3(linePoint2.x, linePoint2.y, linePoint2.z);
        } else {
            const offsetPoints = this.createPerpendicularOffsetPoints(p1, p2);
            [lineP1, lineP2] = offsetPoints;
        }

        this.addLine([lineP1, lineP2]);

        this.gfx.drawDashedLine(p1,
            lineP1,
            DASH_SIZE,
            GAP_SIZE,
            dashMaterial,
            LINE_WIDTH);

        this.gfx.drawDashedLine(p2,
            lineP2,
            DASH_SIZE,
            GAP_SIZE,
            dashMaterial,
            LINE_WIDTH);

        this.createEndpoint(p1);
        this.createEndpoint(p2);

        this.createMeasurementLabel(this.midPoint(lineP1, lineP2), value);
    }

    private midPoint(p1: THREE.Vector3, p2: THREE.Vector3): THREE.Vector3 {
        return new THREE.Vector3((p1.x + p2.x) / 2, (p1.y + p2.y) / 2, (p1.z + p2.z) / 2);
    }

    private createEndpoint(position: THREE.Vector3) {

        const label = document.createElement("div");
        label.className = "measure-label visible";

        const labelIcon = document.createElement("div");
        labelIcon.className = "measure-label-icon";
        label.appendChild(labelIcon);

        this.endpoints.push({ position: position, label: label });
        this.viewer.container.appendChild(label);
    };

    private addLine(points) {

        const geometry = new THREE.Geometry();

        for (let point of points) {
            geometry.vertices.push(new THREE.Vector3(point.x, point.y, point.z));
        }
        this.gfx.drawLineAsCylinder(geometry, lineMaterial, LINE_WIDTH);
    }

    load(): boolean {
        console.log(`${MeasurementExtension.extensionName} is loaded!`);
        return super.load();
    };

    unload(): boolean {
        //this.viewer.getToolbar(false).removeControl(this.subToolbar);
        return super.unload();
    };

    onCameraChange() {
        this.updateLabelsPosition(this.endpoints);
        this.updateLabelsPosition(this.labels);
        this.gfx.update();
    };

    updateLabelsPosition(labels) {
        for (let labelData of labels) {

            const label = labelData.label;
            const point = labelData.position;

            if (label && point) {
                const xy = this.project(point, this.viewer);

                xy.x = xy.x - label.getBoundingClientRect().width / 2;
                xy.y = xy.y - label.getBoundingClientRect().height / 2;

                label.style.top = xy.y + "px";
                label.style.left = xy.x + "px";
                label.point = point;
            }
        }
    }

    project(point, viewer, offset?) {
        const camera = viewer.navigation.getCamera();
        const containerBounds = viewer.navigation.getScreenViewport();
        let p = new THREE.Vector3(point.x, point.y, point.z);

        p = p.project(camera);

        offset = offset || 0;

        return new THREE.Vector2(Math.round((p.x + 1) / 2 * containerBounds.width) + offset,
            Math.round((-p.y + 1) / 2 * containerBounds.height) + offset);
    };

    private createMeasurementLabel(position, content) {
        const label = document.createElement("div");
        label.className = "measure-length visible";

        const text = document.createElement("div");
        text.className = "measure-length-text";
        label.appendChild(text);
        text.innerText = content;

        this.viewer.container.appendChild(label);

        this.labels.push({ position: position, label: label });
    };
}